import { useEffect } from "react";
import { Plus, Trash2, X, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { AutomationTrigger } from "@/types/Automation";
import { Handle, Position, type Node, type NodeProps } from "@xyflow/react";
import { DEFAULT_PUBLIC_REPLIES, TRIGGER_OPTIONS } from "./constants";
import type { TriggerNodeData } from "./types";

export function TriggerNode({ data }: NodeProps<Node<TriggerNodeData>>) {
  const { trigger, onTriggerChange, onDeleteTrigger } = data;
  const igData = trigger?.type === "instagram.comment" ? trigger.data : undefined;
  const keywords = igData?.keywords ?? [];
  const keywordMatch = igData?.keywordMatch ?? "startsWith";
  const publicReplies =
    igData?.publicReplies && igData.publicReplies.length > 0 ? igData.publicReplies : DEFAULT_PUBLIC_REPLIES;

  const handleTypeChange = (type: AutomationTrigger["type"]) => {
    if (type === "instagram.comment") {
      onTriggerChange({
        type: "instagram.comment",
        data: {
          commentType: "keywords",
          keywords: [],
          publicReplies: [...DEFAULT_PUBLIC_REPLIES],
        },
      });
    } else {
      onTriggerChange({ type });
    }
  };

  const updateIgData = (updates: NonNullable<AutomationTrigger["data"]>) => {
    if (trigger?.type !== "instagram.comment") return;
    onTriggerChange({ ...trigger, data: { ...igData, ...updates } });
  };

  useEffect(() => {
    if (trigger?.type !== "instagram.comment" || trigger.data?.commentType !== "any") return;
    onTriggerChange({
      ...trigger,
      data: { ...trigger.data, commentType: "keywords" },
    });
  }, [trigger, onTriggerChange]);

  return (
    <div className="bg-white rounded-xl border-2 border-orange-300 shadow-md w-[320px] relative pb-10 nopan draggable selectable">
      <div className="flex items-center justify-between px-4 py-3 bg-orange-50 rounded-t-xl border-b border-orange-200">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-orange-500" />
          <span className="font-semibold text-sm text-orange-700">Trigger</span>
        </div>
        <button
          type="button"
          onClick={onDeleteTrigger}
          onPointerDownCapture={(e) => e.stopPropagation()}
          className="text-gray-400 hover:text-red-500 transition-colors"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <div className="p-4 space-y-4" onPointerDownCapture={(e) => e.stopPropagation()}>
        <Select
          value={trigger?.type || ""}
          onValueChange={(val) => handleTypeChange(val as AutomationTrigger["type"])}
        >
          <SelectTrigger className="w-full" data-testid="automation-trigger-select">
            <SelectValue placeholder="Select a trigger..." />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>Engram</SelectLabel>
              {TRIGGER_OPTIONS.filter((o) => o.section === "engram").map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  data-testid={`automation-trigger-option-${opt.value}`}
                >
                  <span className="flex items-center gap-2">
                    {opt.icon}
                    {opt.label}
                  </span>
                </SelectItem>
              ))}
            </SelectGroup>
            <SelectGroup>
              <SelectLabel>Instagram</SelectLabel>
              {TRIGGER_OPTIONS.filter((o) => o.section === "instagram").map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  data-testid={`automation-trigger-option-${opt.value}`}
                >
                  <span className="flex items-center gap-2">
                    {opt.icon}
                    {opt.label}
                  </span>
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>

        {trigger?.type === "instagram.comment" && (
          <div className="space-y-4 pt-2 border-t border-orange-100">
            <div>
              <div className="text-xs font-medium text-orange-700 mb-2">Keywords</div>
              <Select
                value={keywordMatch}
                onValueChange={(val) =>
                  updateIgData({ keywordMatch: val as "startsWith" | "includes" })
                }
              >
                <SelectTrigger className="w-full mb-2" data-testid="automation-keyword-match">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="startsWith">Starts with</SelectItem>
                  <SelectItem value="includes">Includes</SelectItem>
                </SelectContent>
              </Select>
              <div className="space-y-2">
                {keywords.map((kw, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      value={kw}
                      onChange={(e) => {
                        const next = [...keywords];
                        next[i] = e.target.value;
                        updateIgData({ keywords: next, commentType: "keywords" });
                      }}
                      placeholder="Keyword"
                      className="flex-1 text-sm"
                      data-testid="automation-keyword-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() =>
                        updateIgData({ keywords: keywords.filter((_, j) => j !== i), commentType: "keywords" })
                      }
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full text-sm"
                  data-testid="automation-add-keyword"
                  onClick={() => updateIgData({ keywords: [...keywords, ""], commentType: "keywords" })}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add keyword
                </Button>
              </div>
            </div>

            <div>
              <div className="text-xs font-medium text-orange-700 mb-2">Public Replies</div>
              <div className="space-y-2">
                {publicReplies.map((reply, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      value={reply}
                      onChange={(e) => {
                        const next = [...publicReplies];
                        next[i] = e.target.value;
                        updateIgData({ publicReplies: next });
                      }}
                      placeholder="Reply text"
                      className="flex-1 text-sm"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => updateIgData({ publicReplies: publicReplies.filter((_, j) => j !== i) })}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full text-sm"
                  onClick={() => updateIgData({ publicReplies: [...publicReplies, ""] })}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add reply
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="absolute bottom-2 right-0 flex items-center">
        <span className="py-0.5 pr-2 rounded-md text-xs font-semibold text-orange-600 bg-white/80">Then</span>
        <Handle
          type="source"
          position={Position.Right}
          className="!bg-orange-400 !w-3 !h-3 !border-2 !border-white"
        />
      </div>
    </div>
  );
}
